// Harmonic analysis: local key centers, functional cells and roman numerals
// for a chord sequence — the middle of "lead sheets → ANALYSIS → chord
// melody". Everything here is derived, pure and positioned: regions and cells
// carry sequence indexes, and each SequenceChord already knows its bar and
// beat, so a presentation lane can place brackets with the engraver's own
// coordinates and the voicing engines can read function instead of guessing.
//
// The key detector is a tiny Viterbi over 24 candidate keys: every chord is
// scored for FUNCTIONAL fit in each key (diatonic sevenths high, dominants
// highest on V, the idiomatic borrowings — tritone subs, backdoor ♭VII7,
// blues I7/IV7 — at reduced credit), and changing key costs a switch penalty,
// so regions come out as phrases rather than per-chord flicker. Giant Steps
// still segments (three tonics, two-chord regions — the fit swings are
// decisive), All The Things You Are yields its textbook key map, and a blues
// stays one region. Those are pinned as test vectors, and a corpus audit
// checks the statistical claim that LOCAL romans explain standards far better
// than global ones.

import { CHORD_TYPES } from './chords';
import { pitchClassOf } from './notes';
import { qualityFamily, degreeLabel, parseKeyLabel } from './corpus';
import type { SequenceChord } from './song';

export interface KeyRegion {
  /** First and last sequence index (inclusive). */
  from: number;
  to: number;
  tonicPc: number;
  minor: boolean;
  /** Display label, e.g. "A♭" or "F minor". */
  label: string;
  /** Mean functional fit of the region's chords (0..10) — a confidence. */
  fit: number;
}

export type HarmonicCellKind = 'ii-V-I' | 'ii-V' | 'V-I' | 'subV-I' | 'backdoor';

export interface HarmonicCell {
  kind: HarmonicCellKind;
  /** Sequence indexes, inclusive; `to` is the resolution where one exists. */
  from: number;
  to: number;
}

export interface HarmonicAnalysis {
  regions: KeyRegion[];
  /** Local-key roman per chord ("II m", "V7", "V7/II", "I∆"). */
  romans: string[];
  cells: HarmonicCell[];
}

const PC_NAMES = ['C', 'D♭', 'D', 'E♭', 'E', 'F', 'G♭', 'G', 'A♭', 'A', 'B♭', 'B'];

const keyLabel = (tonicPc: number, minor: boolean): string =>
  minor ? `${PC_NAMES[tonicPc]} minor` : PC_NAMES[tonicPc];

type Family = ReturnType<typeof qualityFamily>;

/**
 * Functional fit of one chord in one key (0..10). The table IS the theory:
 * diatonic functions score high, the standard-practice borrowings score
 * mid, chromatic strangers score ~0. Blues tonic/subdominant sevenths (I7,
 * IV7) get real credit so a blues reads as ONE key, not a modulation every
 * bar.
 */
function fit(degree: number, family: Family, minor: boolean): number {
  if (!minor) {
    switch (degree) {
      case 0: // I
        return family === '∆' ? 10 : family === '7' ? 6 : family === 'm' ? 3 : 1;
      case 2: // ii
        return family === 'm' ? 9 : family === 'ø' ? 5 : family === '7' ? 5 : 1;
      case 4: // iii / V7-of-vi
        return family === 'm' ? 7 : family === '7' ? 4 : 1;
      case 5: // IV
        return family === '∆' ? 8 : family === '7' ? 6 : family === 'm' ? 5 : 1;
      case 7: // V
        return family === '7' ? 10 : family === 'm' ? 4 : family === '∆' ? 3 : 1;
      case 9: // vi / V7-of-ii
        return family === 'm' ? 8 : family === '7' ? 4 : 1;
      case 11: // vii / V7-of-iii; vii m7 the ii of a chromatic ii–V from below
        return family === 'ø'
          ? 7
          : family === '°'
            ? 6
            : family === '7'
              ? 3
              : family === 'm'
                ? 2
                : 0;
      case 1: // ♭II: tritone sub; ♯i°7 the passing diminished up to ii
        return family === '7' ? 5 : family === '°' ? 4 : family === '∆' ? 3 : 0;
      case 3: // ♭III / ♯ii°; ♭iii m7 the ii of a chromatic ii–V from above
        return family === '°'
          ? 5
          : family === '∆'
            ? 4
            : family === '7'
              ? 3
              : family === 'm'
                ? 3
                : 0;
      case 6: // ♯iv
        return family === 'ø' ? 5 : family === '°' ? 5 : family === '7' ? 3 : 0;
      case 8: // ♭VI; ♭vi m7 the ii of the sub ii–V (A♭m7 D♭7 → C)
        return family === '∆' ? 4 : family === '7' ? 4 : family === 'm' ? 3 : 0;
      case 10: // ♭VII: backdoor
        return family === '7' ? 6 : family === '∆' ? 3 : 0;
      default:
        return 0;
    }
  }
  switch (degree) {
    case 0: // i
      return family === 'm' ? 10 : family === '7' ? 3 : 1;
    case 2: // iiø (or dorian ii)
      return family === 'ø' ? 9 : family === 'm' ? 6 : family === '7' ? 4 : 1;
    case 3: // ♭III
      return family === '∆' ? 7 : family === '7' ? 3 : 1;
    case 5: // iv
      return family === 'm' ? 7 : family === '7' ? 5 : 1;
    case 7: // V
      return family === '7' ? 10 : family === 'm' ? 5 : 1;
    case 8: // ♭VI
      return family === '∆' ? 7 : family === '7' ? 4 : 0;
    case 10: // ♭VII
      return family === '7' ? 6 : family === '∆' ? 4 : 0;
    case 11: // vii°
      return family === '°' ? 6 : family === 'ø' ? 4 : family === '7' ? 3 : 0;
    case 1:
      return family === '7' ? 5 : 0;
    default:
      return 0;
  }
}

/** A key change must buy at least this much total fit. Tuned on the vectors:
 * high enough that a blues holds one region (Blues For Alice's opening
 * Eø–A7–Dm gains exactly 7 as a D-minor detour — a real key must beat a
 * passing tonicization by more than a coin flip), low enough that Giant
 * Steps' two-chord tonic regions still separate (those swings gain 13+). */
const SWITCH_PENALTY = 9;

/**
 * Home-key gravity: a standing bonus for the chart's own key on EVERY chord.
 * A bare ii–V is a pointer, not a key — but each pair in a bird-blues chain
 * (Eø–A7–Dm, Cm–F7, B♭m–E♭7…) contains a local cadence and outscores F
 * chord-for-chord, so without a prior the form reads as six keys in twelve
 * bars. This is the prior a human ear brings to a tune CALLED a blues in F:
 * when in doubt, it is still F. Real modulations (ATTYA's five keys, Giant
 * Steps' three) gain far more than the gravity costs them.
 */
const HOME_BONUS = 1.5;

interface KeyState {
  tonicPc: number;
  minor: boolean;
}

const KEYS: KeyState[] = Array.from({ length: 24 }, (_, i) => ({
  tonicPc: i % 12,
  minor: i >= 12,
}));

const mod12 = (n: number) => ((n % 12) + 12) % 12;

/** How a chord FUNCTIONS: the root and quality family a listener assigns it,
 *  which for a slash chord is not always what is written above the slash. */
export interface FunctionalReading {
  rootPc: number;
  family: Family;
  /** A hybrid with no third above its bass — the sus sound (Gm7/C, F/G). */
  sus: boolean;
}

/**
 * A slash chord is read from its BASS when the bass is not one of the upper
 * chord's tones — a hybrid (Gm7/C is a tonic sus, Bm/C a Lydian tonic, F/G a
 * G7sus), not a G minor or a B minor. Two exceptions keep the written root:
 * an INVERSION (the bass is a chord tone — C∆/G, E7/B, F∆/C over a pedal),
 * and a chord whose neighbour shares its root, which is the same chord with
 * the bass WALKING under it (Am Am/G Am/F Am/E: the line cliché stays IVm,
 * not a G-something on every beat). The family of a bass-rooted reading is
 * derived from the intervals the upper tones make over the bass.
 */
export function functionalReading(
  sequence: readonly SequenceChord[],
  i: number,
): FunctionalReading {
  const chord = sequence[i];
  const rootPc = pitchClassOf(chord.root);
  const written = { rootPc, family: qualityFamily(chord.chordType), sus: false };
  if (!chord.bass) return written;
  const bassPc = pitchClassOf(chord.bass);
  if (bassPc === rootPc) return written;
  const upper = new Set(
    [0, ...(CHORD_TYPES[chord.chordType]?.intervals ?? [])].map((iv) => mod12(rootPc + iv)),
  );
  if (upper.has(bassPc)) return written;
  // The SAME chord next door (root and family both) — E7 then Em/A is not a
  // walking bass under one chord, it is a new chord on the A.
  const neighbourShares = [sequence[i - 1], sequence[i + 1]].some(
    (n) =>
      n &&
      n.root &&
      pitchClassOf(n.root) === rootPc &&
      qualityFamily(n.chordType) === written.family,
  );
  if (neighbourShares) return written;
  // A DOMINANT a fifth above its bass is the V over a tonic pedal (G7/C),
  // heard as the dominant it is written as — where the minor seventh a fifth
  // above (Gm7/C) is the tonic sus, and reads from the bass below.
  if (written.family === '7' && mod12(rootPc - bassPc) === 7) return written;
  const has = (iv: number) => upper.has(mod12(bassPc + iv));
  const third = has(4) ? 'major' : has(3) ? 'minor' : null;
  let family: Family;
  if (third === 'minor') family = has(6) && !has(7) ? (has(10) ? 'ø' : '°') : 'm';
  else if (third === 'major') family = has(10) ? '7' : '∆';
  // No third: the ♭7 or the 4 makes it a sus (F/G, C/D, F♯m/E); a major
  // seventh or bare upper extensions make it the Lydian colour (Bm/C, D/C).
  else family = has(10) || (has(5) && !has(11)) ? '7' : '∆';
  return { rootPc: bassPc, family, sus: third === null };
}

function chordFit(chord: SequenceChord, key: KeyState, reading?: FunctionalReading): number {
  const r = reading ?? { rootPc: pitchClassOf(chord.root), family: qualityFamily(chord.chordType) };
  const degree = mod12(r.rootPc - key.tonicPc);
  return fit(degree, r.family, key.minor);
}

/** Raw functional fit of a chord in a key (0..10) — no gravity, no context.
 *  Exported for the corpus audit and any consumer scoring hypotheses. */
export function functionalFit(chord: SequenceChord, tonicPc: number, minor: boolean): number {
  return chordFit(chord, { tonicPc, minor });
}

/**
 * Segment a performance-order chord sequence into local key regions.
 * `globalKey` (the chart's key field) breaks ties toward the tune's home.
 */
export function keyRegions(sequence: SequenceChord[], globalKey?: string): KeyRegion[] {
  if (sequence.length === 0) return [];
  let home: { tonicPc: number; minor: boolean } = parseKeyLabel(globalKey);
  // The minor-mode prior: importers stamp a chart's key from its SIGNATURE,
  // and a signature cannot say "B♭ minor" — Nica's Dream arrives as "Db".
  // When a major-stamped form both opens and closes on the relative minor's
  // tonic (a minor-family chord on that root), the tune is telling us its
  // mode; aim the gravity at the relative minor instead. One-directional on
  // purpose: signatures under-report minor, never the reverse. ATTYA is the
  // guard case — it OPENS on Fm7 but closes on A♭∆, so it keeps its major
  // home and the opening reads as vi, not as an F-minor tune.
  if (!home.minor) {
    const relativePc = (home.tonicPc + 9) % 12;
    const minorTonic = (chord: SequenceChord) =>
      pitchClassOf(chord.root) === relativePc && qualityFamily(chord.chordType) === 'm';
    if (minorTonic(sequence[0]) && minorTonic(sequence[sequence.length - 1])) {
      home = { tonicPc: relativePc, minor: true };
    }
  }
  const homeIndex = home.tonicPc + (home.minor ? 12 : 0);

  // Viterbi: maximise Σ fit − switches. Ties break toward staying put, then
  // toward the global key, then toward major — deterministic and musical.
  const fits = sequence.map((chord, i) => {
    const reading = functionalReading(sequence, i);
    return KEYS.map((key, k) => chordFit(chord, key, reading) + (k === homeIndex ? HOME_BONUS : 0));
  });
  let scores = [...fits[0]];
  const back: number[][] = [KEYS.map(() => -1)];
  for (let i = 1; i < sequence.length; i++) {
    const next: number[] = [];
    const pointers: number[] = [];
    for (let k = 0; k < KEYS.length; k++) {
      let best = -Infinity;
      let bestPrev = -1;
      for (let p = 0; p < KEYS.length; p++) {
        const candidate = scores[p] - (p === k ? 0 : SWITCH_PENALTY);
        const better =
          candidate > best ||
          (candidate === best && (p === k || (bestPrev !== k && p === homeIndex)));
        if (better) {
          best = candidate;
          bestPrev = p;
        }
      }
      next.push(best + fits[i][k]);
      pointers.push(bestPrev);
    }
    scores = next;
    back.push(pointers);
  }

  let k = 0;
  for (let i = 1; i < KEYS.length; i++) {
    if (scores[i] > scores[k] || (scores[i] === scores[k] && i === homeIndex)) k = i;
  }
  const picks: number[] = new Array(sequence.length);
  for (let i = sequence.length - 1; i >= 0; i--) {
    picks[i] = k;
    k = back[i][k];
  }

  // A single-chord region is a pull, not a key — absorb it into whichever
  // neighbour explains it better (ties to the earlier one).
  for (let i = 0; i < picks.length; i++) {
    const prev = i > 0 ? picks[i - 1] : null;
    const next = i + 1 < picks.length ? picks[i + 1] : null;
    if (prev !== picks[i] && next !== picks[i] && (prev != null || next != null)) {
      const prevFit = prev != null ? fits[i][prev] : -1;
      const nextFit = next != null ? fits[i][next] : -1;
      picks[i] = prevFit >= nextFit ? (prev ?? next!) : (next ?? prev!);
    }
  }

  const regions: KeyRegion[] = [];
  for (let i = 0; i < picks.length; i++) {
    const key = KEYS[picks[i]];
    const last = regions[regions.length - 1];
    if (last && last.tonicPc === key.tonicPc && last.minor === key.minor) {
      last.to = i;
      last.fit += fits[i][picks[i]];
    } else {
      regions.push({
        from: i,
        to: i,
        tonicPc: key.tonicPc,
        minor: key.minor,
        label: keyLabel(key.tonicPc, key.minor),
        fit: fits[i][picks[i]],
      });
    }
  }
  for (const region of regions) region.fit /= region.to - region.from + 1;
  return regions;
}

/** Functional cells with positions. Longest first per start: a ii–V–I owns
 * its span, so the bare ii–V and V–I inside it are not re-reported. Read
 * through `functionalReading`, so Dm7/G is the V of a V–I and Gm7/C the I. */
export function harmonicCells(sequence: SequenceChord[], regions: KeyRegion[]): HarmonicCell[] {
  const regionAt = (i: number) => regions.find((r) => i >= r.from && i <= r.to);
  const readings = sequence.map((_, i) => functionalReading(sequence, i));
  const up = (i: number, j: number) => mod12(readings[j].rootPc - readings[i].rootPc);
  const familyAt = (i: number) => readings[i].family;
  const isMinorFamily = (i: number) => ['m', 'ø'].includes(familyAt(i));
  const cells: HarmonicCell[] = [];
  const covered = new Set<number>();

  for (let i = 0; i < sequence.length; i++) {
    if (covered.has(i)) continue;
    const hasB = i + 1 < sequence.length;
    const hasC = i + 2 < sequence.length;

    // ii–V(–I): minor-family, up a fourth to a dominant, optionally resolving
    // another fourth up to a tonic-family chord.
    if (hasB && isMinorFamily(i) && familyAt(i + 1) === '7' && up(i, i + 1) === 5) {
      const resolves = hasC && up(i + 1, i + 2) === 5 && ['∆', 'm', '7'].includes(familyAt(i + 2));
      const to = resolves ? i + 2 : i + 1;
      cells.push({ kind: resolves ? 'ii-V-I' : 'ii-V', from: i, to });
      for (let j = i; j <= to; j++) covered.add(j);
      continue;
    }
    if (!hasB || familyAt(i) !== '7') continue;

    // subV–I: dominant falling a half step onto a chord that belongs here.
    if (up(i, i + 1) === 11 && (regionAt(i + 1)?.fit ?? 0) > 0 && familyAt(i + 1) !== '7') {
      cells.push({ kind: 'subV-I', from: i, to: i + 1 });
      covered.add(i).add(i + 1);
      continue;
    }
    // Backdoor: ♭VII7 up a step onto the local tonic major.
    const region = regionAt(i + 1);
    if (
      region &&
      up(i, i + 1) === 2 &&
      familyAt(i + 1) === '∆' &&
      readings[i + 1].rootPc === region.tonicPc
    ) {
      cells.push({ kind: 'backdoor', from: i, to: i + 1 });
      covered.add(i).add(i + 1);
      continue;
    }
    // Plain V–I.
    if (up(i, i + 1) === 5 && ['∆', 'm'].includes(familyAt(i + 1))) {
      cells.push({ kind: 'V-I', from: i, to: i + 1 });
      covered.add(i).add(i + 1);
    }
  }
  return cells;
}

/** The quality mark a roman carries: the family symbol, except that a TRIAD
 *  prints the way a lead sheet writes one (I, IIm, VII°, ♭VI+ — never I∆). */
function romanSuffix(chord: SequenceChord, reading: FunctionalReading): string {
  if (reading.sus) return reading.family === '7' ? 'sus' : '∆';
  if (reading.rootPc === pitchClassOf(chord.root)) {
    const type = CHORD_TYPES[chord.chordType];
    if (type?.category === 'triad') {
      const marks: Record<string, string> = { maj: '', min: 'm', dim: '°', aug: '+' };
      return marks[chord.chordType] ?? '';
    }
  }
  return reading.family === 'm' ? 'm' : reading.family;
}

/** Local-key romans, with secondary-dominant notation where a non-V dominant
 * resolves down a fifth inside its region ("V7/II"). */
export function romanAnalysis(sequence: SequenceChord[], regions: KeyRegion[]): string[] {
  const regionAt = (i: number) => regions.find((r) => i >= r.from && i <= r.to);
  const readings = sequence.map((_, i) => functionalReading(sequence, i));
  return sequence.map((chord, i) => {
    const region = regionAt(i);
    if (!region) return '';
    const reading = readings[i];
    const degree = degreeLabel(reading.rootPc, region.tonicPc);
    const next = readings[i + 1];
    if (
      reading.family === '7' &&
      !reading.sus &&
      degree !== 'V' &&
      next &&
      mod12(next.rootPc - reading.rootPc) === 5 &&
      regionAt(i + 1) === region
    ) {
      const target = degreeLabel(next.rootPc, region.tonicPc);
      if (target !== 'I') return `V7/${target}`;
    }
    return `${degree}${romanSuffix(chord, reading)}`;
  });
}

/** The whole picture in one call — what the score's analysis lane consumes. */
export function analyzeHarmony(sequence: SequenceChord[], globalKey?: string): HarmonicAnalysis {
  const regions = keyRegions(sequence, globalKey);
  return {
    regions,
    romans: romanAnalysis(sequence, regions),
    cells: harmonicCells(sequence, regions),
  };
}
